/**
 * @fileoverview Shared appearance + honest-absence contract for the three
 * renderings of the Event v2 decision payload:
 *
 * | Rendering                    | Band      | Owner   |
 * | ---------------------------- | --------- | ------- |
 * | `EventDecisionPanelView`     | ≥1024     | WEB-706 |
 * | `EventDecisionBarView` (bar) | 768–1023  | WEB-708 |
 * | `…` (`bottom-bar`)           | ≤767      | WEB-709 |
 *
 * All three read the same `EventDetailV2.decisionPanel` object, so the CTA's
 * href, its note, and the two "no honest value" strings must resolve
 * identically at every width. Keeping them here rather than copied per file is
 * what makes that structural instead of aspirational — a reader who sees
 * "Time TBA" on desktop and something else on mobile is being told the page
 * changed its mind about what it knows.
 *
 * The class constants are only the parts that are genuinely shared: colour,
 * type, and the interaction states. **Layout is deliberately NOT here** — the
 * panel CTA is full-width in a 400px rail, the bar CTA is `flex: none` hard
 * right in a horizontal strip. Each call site adds its own sizing.
 */

/** "Time TBA" — shown whenever no honest time string is available (WEB-706 / API-1663). */
export const HEADLINE_FALLBACK = 'Time TBA';

/** Shown when the server supplied no ticket link and no `cta.note` (WEB-1190 D6). */
export const DISABLED_CTA_FALLBACK_NOTE = 'No ticket link listed';

/**
 * The enabled (real destination) CTA's shared appearance. Two details in here
 * are load-bearing and were each a real bug before they were pinned:
 *
 * - **`[font-family:…]`, not the `font-[…]` shorthand.** tailwind-merge buckets
 *   the shorthand in the same conflict group as `font-bold`/`font-semibold` and
 *   silently drops whichever comes first when both ride through `cn()`, which
 *   loses the display family. The bracket CSS-property form sidesteps that
 *   group entirely (same reason `EVT_MICRO_LABEL_*` in `eventV2MicroLabel.ts`
 *   uses it).
 * - **`text-[color:var(--cta-fg)]` — the `color:` hint, and no `!`.** This
 *   used to be `text-[var(--cta-fg)]!`, and the `!` was load-bearing: an
 *   unlayered `a { color: inherit }` reset in `globals.css` beat every layered
 *   text-colour utility on an `<a>`, so without `!` the anchor inherited the
 *   ambient page ink (axe color-contrast: ~1.7:1 white-on-amber in dark).
 *   WEB-853 moved that reset into `@layer base`, so a plain utility wins
 *   normally and the `!` is gone — it was inverting layer order, which makes
 *   the NEXT override harder, not easier. The explicit `color:` hint stays:
 *   a bare `var()` inside `text-[…]` is ambiguous, and `cn()`'s tailwind-merge
 *   buckets the ambiguous form with font-size, dropping one of the two when
 *   both ride through.
 *
 * No bare `focus-visible:outline` either — `outline-2` plus a colour already
 * implies the solid outline style in Tailwind v4, and `cn()` drops the bare
 * utility as a same-group conflict with `outline-2` anyway (the established
 * pattern in `ProviderTicketButton` / `SettingsSidebar`).
 */
// WEB-739: `--r-sm` (4px) + `gap-[10px]` — the design's CTA proportions. Do
// not walk these back to `--r-md` / `gap-2`; at 400×48 the 4px radius is
// what reads as a crisp rectangle rather than a rounded pill.
export const DECISION_CTA_LINK_CLASS = [
  'group flex items-center justify-center gap-[10px] rounded-ds-sm bg-cta',
  '[font-family:var(--font-display)] font-bold text-cta-fg',
  'transition-[background-color,transform] duration-micro motion-reduce:transition-none',
  'hover:bg-cta-hover',
  'active:scale-[0.98] active:duration-press',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cta',
].join(' ');

/**
 * The disabled (`cta.href === null`) CTA's shared appearance. Rendered on a
 * bare `<div>` at every call site — never a `<button>`, never a dead `#` link
 * — so it carries no interaction states by design.
 */
export const DECISION_CTA_DISABLED_CLASS = [
  'flex select-none items-center justify-center gap-[10px] rounded-ds-sm',
  'bg-ds-surface-alt cursor-not-allowed',
  '[font-family:var(--font-display)] font-bold text-fg-3',
].join(' ');

/** The minimum touch height every decision CTA honours, at every width. */
export const DECISION_CTA_MIN_HEIGHT_CLASS = 'min-h-[48px]';

/** The `cta` sub-object of the server-assembled `EventDecisionPanel` payload. */
interface DecisionCta {
  label: string;
  href: string | null;
  note: string | null;
}

/**
 * The single resolution of "what note goes with this CTA", shared by all three
 * renderings.
 *
 * - No CTA at all (absent payload) → `null`; the caller renders no CTA either.
 * - `href === null` → the server's own `cta.note` when it says something,
 *   otherwise {@link DISABLED_CTA_FALLBACK_NOTE}. A disabled CTA is never left
 *   unexplained. When the fallback is the note, callers hide the button
 *   ({@link shouldHideDisabledDecisionCta}).
 * - Otherwise → the server's `cta.note` verbatim, `null` included.
 */
export function resolveDecisionCtaNote(cta: DecisionCta | null | undefined): string | null {
  if (!cta) return null;
  if (cta.href !== null) return cta.note;
  return cta.note?.trim() ? cta.note : DISABLED_CTA_FALLBACK_NOTE;
}

/**
 * WEB-1190 D6 — a missing ticket link with no server note is a sentence, not
 * a disabled "Get tickets" control. A real `cta.note` (for example "On sale
 * Friday at 10am") keeps the disabled button.
 */
export function shouldHideDisabledDecisionCta(
  cta: Pick<DecisionCta, 'href' | 'note'> | null | undefined,
): boolean {
  if (!cta || cta.href !== null) return false;
  return !cta.note?.trim();
}
